import { TextField } from '@issuu/ixu-react';
import { colorBaseWhite, layoutMarginSmall, ProductButton } from '@issuu/silkscreen';
import React, { useCallback, useContext, useRef, useState } from 'react';
import styled from 'styled-components';

import { breakpointWidths } from '../../constants';
import DocumentContext from '../DocumentPage/DocumentContext';
import { useClickOutside } from '../shared/click-outside';
import { MenuBox } from './MenuBox';
import type { ShareType } from './SelectShareType';
import { SelectShareType } from './SelectShareType';
import { ShareBoxPreview } from './ShareBoxPreview';
import { ShareboxSocials } from './ShareBoxSocials';

const ShareOptions = styled.div`
    margin: ${layoutMarginSmall};
    @media (max-width: ${breakpointWidths.smallMax}px) {
        flex-grow: 1;
        flex-shrink: 1;
    }
    display: inline-flex;
    flex-direction: column;
    justify-content: space-between;
`;

const CopyField = styled.div`
    margin: ${layoutMarginSmall} 0;
    position: relative;
    width: 100%;

    > input {
        padding-right: 4.6rem;
    }
`;

const CopyButton = styled.div`
    position: absolute;
    right: 0.1em;
    top: 0.15em;
    background-color: ${colorBaseWhite};
    width: 4.5rem;
`;

const getShareableUrl = (
    baseUrl: string,
    shareType: ShareType,
    givenPageNumber: number,
    shareArticleId: number | undefined,
): string => {
    if (shareType === 'page' && givenPageNumber > 1) {
        return `${baseUrl}/${givenPageNumber}`;
    }
    if (shareType === 'article' && typeof shareArticleId !== 'undefined') {
        return `${baseUrl}/s/${shareArticleId}`;
    }
    // when page is 1 (cover), use url without page
    return baseUrl;
};

type Props = {
    closeHandler: () => void;
};

export const ShareBox: React.FC<Props> = ({ closeHandler }: Props) => {
    const shareBoxRef = useRef<HTMLDivElement>(null);

    const {
        document: { documentName, pageCount },
        hosts: { main },
        publisher: { username },
        articleStorySummaries,
        appState: { currentPageNumbers },
    } = useContext(DocumentContext);

    const articleHasPages = articleStorySummaries.filter(
        article =>
            article.pageNumbers.filter(articlePage => currentPageNumbers[0].includes(articlePage))
                .length,
    );

    const [givenPageNumber, setGivenPageNumber] = useState<number>(currentPageNumbers[0][0]);
    const [shareArticleId, setShareArticleId] = useState<number | undefined>(
        articleHasPages.length ? articleHasPages[0].id : articleStorySummaries[0]?.id,
    );
    const [copied, setCopied] = useState(false);
    const [shareType, setShareType] = useState<ShareType>('cover');
    useClickOutside(shareBoxRef, closeHandler);
    const baseShareUrl = `https://${main}/${username}/docs/${documentName}`;
    const shareLink = getShareableUrl(baseShareUrl, shareType, givenPageNumber, shareArticleId);
    const copyUrl = useCallback(() => {
        navigator.clipboard.writeText(
            getShareableUrl(baseShareUrl, shareType, givenPageNumber, shareArticleId),
        );
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    }, [shareType, givenPageNumber, shareArticleId]);

    return (
        <MenuBox ref={shareBoxRef}>
            <ShareOptions>
                <SelectShareType
                    articleHasPages={articleHasPages}
                    givenPageNumber={givenPageNumber}
                    pageCount={pageCount}
                    setGivenPageNumber={setGivenPageNumber}
                    setShareArticleId={setShareArticleId}
                    setShareType={setShareType}
                    shareType={shareType}
                />
                <CopyField>
                    <TextField onChange={() => undefined} value={shareLink} />
                    <CopyButton onClick={copyUrl}>
                        <ProductButton variant="ghost">{copied ? 'COPIED!' : 'COPY'}</ProductButton>
                    </CopyButton>
                </CopyField>
                <ShareboxSocials
                    givenPageNumber={givenPageNumber}
                    shareLink={shareLink}
                    shareType={shareType}
                />
            </ShareOptions>
            <ShareBoxPreview pageNumber={givenPageNumber} />
        </MenuBox>
    );
};
